<template>
  <div class="prev-shell">
    <!-- Toolbar -->
    <div class="prev-bar">
      <button class="prev-close" @click="$emit('close')">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>
        Fechar
      </button>

      <div class="prev-bar-center">
        <span class="prev-bar-title">Visualização de Impressão</span>
        <div class="prev-lang-wrap">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="8" r="6.5"/><path d="M8 1.5C6 4 5 6 5 8s1 4 3 6.5M8 1.5C10 4 11 6 11 8s-1 4-3 6.5M1.5 8h13" stroke-linecap="round"/></svg>
          <select v-model="lang" class="prev-lang-sel">
            <option v-for="l in LANGS" :key="l.code" :value="l.code">{{ l.flag }} {{ l.name }}</option>
          </select>
        </div>
      </div>

      <div class="prev-bar-right">
        <button class="prev-tool-btn" :class="{ 'prev-tool-btn--on': editMode }" @click="editMode = !editMode">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 2l2 2L5 11H3V9L10 2z" stroke-linejoin="round"/><path d="M8.5 3.5l2 2"/></svg>
          {{ editMode ? 'Editando' : 'Editar' }}
        </button>

        <!-- Save button with status feedback -->
        <button
          class="prev-save-btn"
          :class="`prev-save-btn--${saveStatus}`"
          :disabled="saveStatus === 'saving'"
          @click="doSave"
        >
          <!-- idle -->
          <template v-if="saveStatus === 'idle'">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 2h8l2 2v8a1 1 0 01-1 1H3a1 1 0 01-1-1V2z"/><path d="M5 2v3h4V2M4 8h6" stroke-linecap="round"/></svg>
            Salvar
          </template>
          <!-- saving -->
          <template v-else-if="saveStatus === 'saving'">
            <svg class="spin" width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="7" r="5.5" stroke-dasharray="16 18"/></svg>
            Salvando…
          </template>
          <!-- saved -->
          <template v-else-if="saveStatus === 'saved'">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M2 7l4 4 6-6"/></svg>
            Salvo
          </template>
          <!-- error -->
          <template v-else>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M7 2v5M7 10v.5"/></svg>
            Erro
          </template>
        </button>

        <div class="prev-bar-sep"></div>

        <button class="prev-export-btn" :disabled="exporting" @click="doExportPdf">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1v8M4 6l3 3 3-3" stroke-linecap="round" stroke-linejoin="round"/><path d="M1 11h12" stroke-linecap="round"/></svg>
          PDF
        </button>
        <button class="prev-export-btn prev-export-btn--word" :disabled="exporting" @click="doExportDoc">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="1" width="10" height="12" rx="1.5"/><path d="M5 4h4M5 7h4M5 10h2" stroke-linecap="round"/></svg>
          Word
        </button>
      </div>
    </div>

    <!-- Edit mode notice -->
    <div v-if="editMode" class="prev-edit-notice">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="8" r="6.5"/><path d="M8 7v4M8 5v.5" stroke-linecap="round"/></svg>
      Clique no texto para editar. Use <strong>P 33% · M 50% · G 100%</strong> ou digite um valor para o tamanho das fotos. Use <strong>⊞</strong> para colocar duas fotos lado a lado.
      <span class="prev-autosave-note">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="6" cy="6" r="4.5"/><path d="M6 3v3l2 1.5" stroke-linecap="round"/></svg>
        Auto-salvo localmente
      </span>
      <span v-if="lang !== 'pt'" class="prev-translate-note">· O corpo será traduzido para {{ LANGS.find(l=>l.code===lang)?.name }} ao exportar.</span>
    </div>

    <!-- Paper scroll area -->
    <div class="prev-scroll">
      <div class="paper">
        <!-- Header band -->
        <div class="paper-band" :style="{ background: accentHex }">
          <div class="band-left">
            <div
              class="band-title"
              :contenteditable="editMode || undefined"
              :data-edit="editMode ? 'true' : undefined"
              @blur="editMode && (headingEdit = ($event.target as HTMLElement).innerText.trim())"
              @keydown.enter.prevent
            >{{ effectiveHeading }}</div>
            <div class="band-meta">
              {{ labels.project }}: {{ doc.attraction || '—' }}
              &nbsp;|&nbsp;{{ labels.date }}: {{ fmtDate(doc.reportDate) }}
              &nbsp;|&nbsp;{{ labels.supervisor }}: {{ doc.author?.name || '—' }}
            </div>
          </div>
          <div class="band-right">
            <div v-if="siteName" class="band-obra">{{ siteName }}</div>
            <div class="band-brand">ObraTycoon by GusMachado</div>
          </div>
        </div>

        <!-- Body -->
        <div class="paper-body">
          <!-- Collaborators -->
          <template v-if="collabs.length">
            <div class="sec-hdr" :style="{ background: accentHex }">{{ labels.collaborators.toUpperCase() }}</div>
            <div v-for="(c, ci) in collabs" :key="`col${ci}`" class="bullet-row">
              <span class="bullet-dot" :style="{ background: accentHex }"></span>
              <span>{{ c }}</span>
            </div>
            <div class="sec-gap"></div>
          </template>

          <!-- Tools -->
          <div class="sec-hdr" :style="{ background: accentHex }">{{ labels.tools.toUpperCase() }}</div>
          <template v-if="tools.length">
            <div v-for="(tool, ti) in tools" :key="`t${ti}`" class="section-block">
              <div class="bullet-row">
                <span class="bullet-dot" :style="{ background: accentHex }"></span>
                <span
                  :contenteditable="editMode || undefined"
                  :data-edit="editMode ? 'true' : undefined"
                  @blur="editMode && toolEdit(ti, $event)"
                  @keydown.enter.prevent
                  class="editable-span"
                >{{ getToolText(ti) }}</span>
              </div>
              <template v-if="tool.images?.length">
                <template v-for="(group, gi) in groupImages(tool.images)" :key="`tg${ti}-${gi}`">
                  <div class="img-row" :class="{ 'img-row--pair': group.length > 1 }">
                    <div
                      v-for="img in group"
                      :key="img"
                      class="img-item"
                      :style="group.length === 1 ? { maxWidth: getImgSize(img) + '%' } : {}"
                    >
                      <img :src="resolveImgUrl(img)" loading="lazy" />
                      <div v-if="editMode" class="img-size-ctrl">
                        <button :class="{ active: getImgSize(img) === 33 }" @click.stop="setImgSize(img, 33)">P</button>
                        <button :class="{ active: getImgSize(img) === 50 }" @click.stop="setImgSize(img, 50)">M</button>
                        <button :class="{ active: getImgSize(img) === 100 }" @click.stop="setImgSize(img, 100)">G</button>
                        <input
                          type="number"
                          min="10"
                          max="100"
                          class="img-size-input"
                          :value="getImgSize(img)"
                          @change.stop="setImgSize(img, Number(($event.target as HTMLInputElement).value))"
                          @click.stop
                        />
                        <span class="img-size-pct">%</span>
                        <button
                          :class="{ active: isPaired(img) }"
                          class="pair-btn"
                          @click.stop="togglePaired(img)"
                          title="Lado a lado com a próxima foto"
                        >⊞</button>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </template>
          <div v-else class="bullet-row">
            <span class="bullet-dot" :style="{ background: accentHex }"></span>
            <span>—</span>
          </div>
          <div class="sec-gap"></div>

          <!-- Activities -->
          <div class="sec-hdr" :style="{ background: accentHex }">{{ labels.activities.toUpperCase() }}</div>
          <template v-if="activities.length">
            <div v-for="(act, ai) in activities" :key="`a${ai}`" class="section-block">
              <div class="act-row">
                <span class="act-num" :style="{ color: accentHex }">{{ ai + 1 }}.</span>
                <span
                  :contenteditable="editMode || undefined"
                  :data-edit="editMode ? 'true' : undefined"
                  @blur="editMode && actEdit(ai, $event)"
                  @keydown.enter.prevent
                  class="editable-span"
                >{{ getActText(ai) }}</span>
              </div>
              <template v-if="act.images?.length">
                <template v-for="(group, gi) in groupImages(act.images)" :key="`ag${ai}-${gi}`">
                  <div class="img-row" :class="{ 'img-row--pair': group.length > 1 }">
                    <div
                      v-for="img in group"
                      :key="img"
                      class="img-item"
                      :style="group.length === 1 ? { maxWidth: getImgSize(img) + '%' } : {}"
                    >
                      <img :src="resolveImgUrl(img)" loading="lazy" />
                      <div v-if="editMode" class="img-size-ctrl">
                        <button :class="{ active: getImgSize(img) === 33 }" @click.stop="setImgSize(img, 33)">P</button>
                        <button :class="{ active: getImgSize(img) === 50 }" @click.stop="setImgSize(img, 50)">M</button>
                        <button :class="{ active: getImgSize(img) === 100 }" @click.stop="setImgSize(img, 100)">G</button>
                        <input
                          type="number"
                          min="10"
                          max="100"
                          class="img-size-input"
                          :value="getImgSize(img)"
                          @change.stop="setImgSize(img, Number(($event.target as HTMLInputElement).value))"
                          @click.stop
                        />
                        <span class="img-size-pct">%</span>
                        <button
                          :class="{ active: isPaired(img) }"
                          class="pair-btn"
                          @click.stop="togglePaired(img)"
                          title="Lado a lado com a próxima foto"
                        >⊞</button>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </template>
          <div v-else class="bullet-row">
            <span class="bullet-dot" :style="{ background: accentHex }"></span>
            <span>—</span>
          </div>
          <div class="sec-gap"></div>

          <!-- Observations -->
          <div class="sec-hdr" :style="{ background: accentHex }">{{ labels.observations.toUpperCase() }}</div>
          <template v-if="observations.length">
            <div v-for="(obs, oi) in observations" :key="`o${oi}`" class="section-block">
              <div class="bullet-row">
                <span class="bullet-dot" :style="{ background: accentHex }"></span>
                <span
                  :contenteditable="editMode || undefined"
                  :data-edit="editMode ? 'true' : undefined"
                  @blur="editMode && obsEdit(oi, $event)"
                  @keydown.enter.prevent
                  class="editable-span"
                >{{ getObsText(oi) }}</span>
              </div>
              <template v-if="obs.images?.length">
                <template v-for="(group, gi) in groupImages(obs.images)" :key="`og${oi}-${gi}`">
                  <div class="img-row" :class="{ 'img-row--pair': group.length > 1 }">
                    <div
                      v-for="img in group"
                      :key="img"
                      class="img-item"
                      :style="group.length === 1 ? { maxWidth: getImgSize(img) + '%' } : {}"
                    >
                      <img :src="resolveImgUrl(img)" loading="lazy" />
                      <div v-if="editMode" class="img-size-ctrl">
                        <button :class="{ active: getImgSize(img) === 33 }" @click.stop="setImgSize(img, 33)">P</button>
                        <button :class="{ active: getImgSize(img) === 50 }" @click.stop="setImgSize(img, 50)">M</button>
                        <button :class="{ active: getImgSize(img) === 100 }" @click.stop="setImgSize(img, 100)">G</button>
                        <input
                          type="number"
                          min="10"
                          max="100"
                          class="img-size-input"
                          :value="getImgSize(img)"
                          @change.stop="setImgSize(img, Number(($event.target as HTMLInputElement).value))"
                          @click.stop
                        />
                        <span class="img-size-pct">%</span>
                        <button
                          :class="{ active: isPaired(img) }"
                          class="pair-btn"
                          @click.stop="togglePaired(img)"
                          title="Lado a lado com a próxima foto"
                        >⊞</button>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </template>
          <div v-else class="bullet-row">
            <span class="bullet-dot" :style="{ background: accentHex }"></span>
            <span>—</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="paper-footer">
          <span>ObraTycoon by GusMachado</span>
          <span>Pré-visualização</span>
        </div>
      </div>
    </div>

    <!-- Export loading overlay -->
    <div v-if="exporting" class="prev-loading">
      <div class="prev-spinner"></div>
      Gerando arquivo…
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from 'vue'
import type { Submission } from '@/store/submissions'
import { submissionsStore } from '@/store/submissions'
import { systemStore } from '@/store/system'
import { machineryStore } from '@/store/machinery'
import { useReportExport } from '@/composables/useReportExport'
import { formatDateOnly } from '@/utils/date'
import { submissionEditState } from '@/store/submissionEditState'

const LANGS = [
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
  { code: 'en', flag: '🇺🇸', name: 'English' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'it', flag: '🇮🇹', name: 'Italiano' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
  { code: 'ja', flag: '🇯🇵', name: '日本語' },
  { code: 'ko', flag: '🇰🇷', name: '한국어' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'nl', flag: '🇳🇱', name: 'Nederlands' },
]

export default defineComponent({
  name: 'SubmissionPrintPreview',
  emits: ['close'],
  props: {
    submission: {
      type: Object as PropType<Submission>,
      required: true,
    },
  },
  setup(props) {
    const cached = submissionEditState.get(props.submission.id)

    const lang = ref<string>(cached?.lang ?? props.submission.reportLanguage ?? 'pt')
    const editMode = ref(false)
    const exporting = ref(false)

    // ── Edit state (restored from cache) ─────────────────────────────────
    const headingEdit = ref(cached?.headingEdit ?? '')
    const toolEdits = ref<Record<number, string>>(cached?.toolEdits ?? {})
    const actEdits = ref<Record<number, string>>(cached?.actEdits ?? {})
    const obsEdits = ref<Record<number, string>>(cached?.obsEdits ?? {})
    const imgSizes = ref<Record<string, number>>(cached?.imgSizes ?? {})
    const imgPaired = ref<Record<string, boolean>>(cached?.imgPaired ?? {})

    // ── Persist edits to cache whenever they change ───────────────────────
    watch(
      [lang, headingEdit, toolEdits, actEdits, obsEdits, imgSizes, imgPaired],
      () => {
        submissionEditState.set(props.submission.id, {
          lang: lang.value,
          headingEdit: headingEdit.value,
          toolEdits: { ...toolEdits.value },
          actEdits: { ...actEdits.value },
          obsEdits: { ...obsEdits.value },
          imgSizes: { ...imgSizes.value },
          imgPaired: { ...imgPaired.value },
        })
      },
      { deep: true }
    )

    // ── Derived data ─────────────────────────────────────────────────────
    const doc = computed(() => props.submission)
    const tools = computed(() => (doc.value.tools || []).filter(t => t.text || t.images?.length))
    const activities = computed(() => doc.value.activities || [])
    const observations = computed(() => (doc.value.observations || []).filter(o => o.text || o.images?.length))
    const collabs = computed(() => (doc.value.collaborators || []).filter(Boolean))

    // ── System config ────────────────────────────────────────────────────
    const accentHex = computed(() => systemStore.state.config.colors?.primary || '#0f4c81')
    const siteName = computed(() => systemStore.state.config.siteName || '')
    const logoText = computed(() => systemStore.getLogoText())

    // ── Export labels ─────────────────────────────────────────────────────
    const localSelectedRef = ref<string[]>([])
    const localDocsRef = ref<Submission[]>([])

    const { exportSelectedDocsAsPdf, exportSelectedDocsAsDoc, reportLabels } = useReportExport({
      selectedDocs: localSelectedRef,
      documentsItems: computed(() => localDocsRef.value),
      formatDateOnly,
      siteName,
      systemColor: accentHex,
      logoText,
    })

    const labels = computed(() => reportLabels(doc.value, lang.value))
    const effectiveHeading = computed(() => headingEdit.value || labels.value.heading)

    // ── Edit helpers ─────────────────────────────────────────────────────
    const resolveMachineTitle = (id: string) => {
      if (!id) return ''
      const m = machineryStore.state.machinery.find(m => m.id === id)
      return m ? m.title : id
    }
    const getToolText = (i: number) => {
      const raw = toolEdits.value[i] !== undefined ? toolEdits.value[i] : (tools.value[i]?.text || '')
      return resolveMachineTitle(raw) || raw
    }
    const getActText = (i: number) => actEdits.value[i] !== undefined ? actEdits.value[i] : (activities.value[i]?.description || '')
    const getObsText = (i: number) => obsEdits.value[i] !== undefined ? obsEdits.value[i] : (observations.value[i]?.text || '')

    const toolEdit = (i: number, evt: Event) => { toolEdits.value[i] = (evt.target as HTMLElement).innerText.trim() }
    const actEdit = (i: number, evt: Event) => { actEdits.value[i] = (evt.target as HTMLElement).innerText.trim() }
    const obsEdit = (i: number, evt: Event) => { obsEdits.value[i] = (evt.target as HTMLElement).innerText.trim() }

    // ── Image size helpers (percentage 1–100) ─────────────────────────────
    const getImgSize = (url: string): number => imgSizes.value[url] ?? 100
    const setImgSize = (url: string, raw: number) => {
      const size = Math.max(10, Math.min(100, Math.round(raw)))
      imgSizes.value = { ...imgSizes.value, [url]: size }
    }

    // ── Image pairing helpers ─────────────────────────────────────────────
    const isPaired = (url: string): boolean => imgPaired.value[url] ?? false
    const togglePaired = (url: string) => {
      imgPaired.value = { ...imgPaired.value, [url]: !isPaired(url) }
    }

    // Groups consecutive paired images into rows of 2; solo otherwise
    const groupImages = (images: string[]): string[][] => {
      const chunks: string[][] = []
      let i = 0
      while (i < images.length) {
        if (isPaired(images[i]) && i + 1 < images.length && isPaired(images[i + 1])) {
          chunks.push([images[i], images[i + 1]])
          i += 2
        } else {
          chunks.push([images[i]])
          i++
        }
      }
      return chunks
    }

    // ── URL resolver ─────────────────────────────────────────────────────
    const resolveImgUrl = (src: string): string => {
      if (!src || src.startsWith('data:')) return src || ''
      if (src.startsWith('/uploads/')) return `${process.env.VUE_APP_API_BASE || 'http://localhost:3001'}${src}`
      return src
    }

    const fmtDate = formatDateOnly

    // ── Image size factors for PDF export ────────────────────────────────
    const imageSizeFactors = computed(() => {
      const map: Record<string, number> = {}
      for (const [url, size] of Object.entries(imgSizes.value)) {
        map[url] = size / 100
      }
      return map
    })

    // ── Build modified doc copy with text edits ──────────────────────────
    const buildEditedDoc = (): Submission => {
      const copy = JSON.parse(JSON.stringify(doc.value)) as Submission
      if (headingEdit.value) copy.title = headingEdit.value
      if (copy.tools) {
        copy.tools = copy.tools.map((t, i) => ({
          ...t,
          text: toolEdits.value[i] !== undefined ? toolEdits.value[i] : t.text,
        }))
      }
      if (copy.activities) {
        copy.activities = copy.activities.map((a, i) => ({
          ...a,
          description: actEdits.value[i] !== undefined ? actEdits.value[i] : a.description,
        }))
      }
      if (copy.observations) {
        copy.observations = copy.observations.map((o, i) => ({
          ...o,
          text: obsEdits.value[i] !== undefined ? obsEdits.value[i] : o.text,
        }))
      }
      return copy
    }

    // ── Save text edits back to the database ────────────────────────────
    type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'
    const saveStatus = ref<SaveStatus>('idle')
    let saveResetTimer: ReturnType<typeof setTimeout> | null = null

    const doSave = async () => {
      saveStatus.value = 'saving'
      if (saveResetTimer) clearTimeout(saveResetTimer)
      try {
        const edited = buildEditedDoc()
        await submissionsStore.updateSubmission(edited.id, {
          title: edited.title,
          tools: edited.tools,
          activities: edited.activities,
          observations: edited.observations,
          reportLanguage: lang.value !== (props.submission.reportLanguage || 'pt') ? lang.value as Submission['reportLanguage'] : props.submission.reportLanguage,
        })
        saveStatus.value = 'saved'
        saveResetTimer = setTimeout(() => { saveStatus.value = 'idle' }, 3000)
      } catch {
        saveStatus.value = 'error'
        saveResetTimer = setTimeout(() => { saveStatus.value = 'idle' }, 4000)
      }
    }

    const doExportPdf = async () => {
      exporting.value = true
      try {
        const editedDoc = buildEditedDoc()
        localDocsRef.value = [editedDoc]
        localSelectedRef.value = [editedDoc.id]
        await exportSelectedDocsAsPdf(lang.value, imageSizeFactors.value, { ...imgPaired.value })
      } finally {
        exporting.value = false
      }
    }

    const doExportDoc = async () => {
      exporting.value = true
      try {
        const editedDoc = buildEditedDoc()
        localDocsRef.value = [editedDoc]
        localSelectedRef.value = [editedDoc.id]
        await exportSelectedDocsAsDoc(lang.value)
      } finally {
        exporting.value = false
      }
    }

    return {
      LANGS, lang, editMode, exporting, saveStatus, doSave,
      doc, tools, activities, observations, collabs,
      accentHex, siteName,
      labels, effectiveHeading,
      headingEdit,
      getToolText, getActText, getObsText,
      toolEdit, actEdit, obsEdit,
      getImgSize, setImgSize,
      isPaired, togglePaired, groupImages,
      resolveImgUrl, fmtDate,
      doExportPdf, doExportDoc,
    }
  },
})
</script>

<style scoped lang="scss">
$accent: var(--system-navbar, #0f4c81);
$border: rgba(31, 30, 26, 0.1);

/* ── Shell ────────────────────────────────────────────────────────── */
.prev-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--navbar-h, 48px));
  background: var(--bg, #f4f3f0);
}

/* ── Toolbar ──────────────────────────────────────────────────────── */
.prev-bar {
  height: 52px;
  background: var(--card, #fff);
  border-bottom: 1px solid $border;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.prev-close {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: var(--ink-muted, #888);
  cursor: pointer;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background .12s;
  &:hover { background: rgba(31,30,26,.05); color: var(--ink, #1a1a1a); }
}

.prev-bar-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.prev-bar-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-muted, #888);
}

.prev-lang-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-muted, #888);
}

.prev-lang-sel {
  background: var(--bg, #f4f3f0);
  border: 1px solid $border;
  color: var(--ink, #1a1a1a);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 13px;
  cursor: pointer;
  outline: none;
  &:focus { border-color: $accent; }
}

.prev-bar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.prev-tool-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1.5px solid $border;
  color: var(--ink, #1a1a1a);
  border-radius: 6px;
  padding: 5px 11px;
  font-size: 12px;
  cursor: pointer;
  transition: background .12s;
  &:hover { background: rgba(31,30,26,.05); }
  &--on {
    background: rgba(15, 76, 129, 0.08);
    color: $accent;
    border-color: rgba(15, 76, 129, 0.3);
  }
}

.prev-save-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1.5px solid $border;
  border-radius: 6px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background .12s, color .12s, border-color .12s;
  background: transparent;
  color: var(--ink, #1a1a1a);

  &:hover:not(:disabled) { background: rgba(31,30,26,.05); }
  &:disabled { cursor: default; }

  &--saving { color: var(--ink-muted, #888); }

  &--saved {
    background: rgba(22, 101, 52, 0.08);
    color: #166534;
    border-color: rgba(22, 101, 52, 0.25);
  }

  &--error {
    background: rgba(153, 27, 27, 0.08);
    color: #991b1b;
    border-color: rgba(153, 27, 27, 0.25);
  }
}

.prev-bar-sep {
  width: 1px;
  height: 22px;
  background: $border;
  flex-shrink: 0;
}

.prev-export-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: $accent;
  border: none;
  color: #fff;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity .12s;
  &:hover { opacity: .88; }
  &:disabled { opacity: .4; cursor: default; }
  &--word { background: #1e6e43; }
}

/* ── Edit notice ──────────────────────────────────────────────────── */
.prev-edit-notice {
  background: rgba(15, 76, 129, 0.06);
  border-bottom: 1px solid rgba(15, 76, 129, 0.15);
  color: var(--ink, #1a1a1a);
  font-size: 12px;
  padding: 7px 20px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.prev-autosave-note {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  color: var(--ink-muted, #888);
  font-size: 11px;
}

.prev-translate-note {
  margin-left: 4px;
  color: #b45309;
}

.spin {
  animation: spin .7s linear infinite;
  transform-origin: center;
}

/* ── Scroll / Paper ───────────────────────────────────────────────── */
.prev-scroll {
  flex: 1;
  padding: 32px 24px 64px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.paper {
  width: 100%;
  max-width: 794px;
  background: #fff;
  box-shadow: 0 4px 24px rgba(0,0,0,.12), 0 1px 4px rgba(0,0,0,.08);
  border-radius: 2px;
  overflow: hidden;
  color: #1f1e1a;
  font-family: Helvetica, Arial, sans-serif;
}

/* ── Header band ──────────────────────────────────────────────────── */
.paper-band {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 40px;
  gap: 20px;
}

.band-left { flex: 1; min-width: 0; }

.band-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  margin-bottom: 6px;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
  &[data-edit] {
    cursor: text;
    border-bottom: 1.5px dashed rgba(255,255,255,.4);
    &:focus { border-color: rgba(255,255,255,.8); }
  }
}

.band-meta {
  font-size: 11px;
  color: rgba(255,255,255,.72);
  line-height: 1.5;
}

.band-right {
  text-align: right;
  flex-shrink: 0;
}

.band-obra {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 3px;
}

.band-brand {
  font-size: 10px;
  color: rgba(255,255,255,.55);
}

/* ── Paper body ───────────────────────────────────────────────────── */
.paper-body {
  padding: 28px 40px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sec-hdr {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #fff;
  padding: 6px 12px;
  margin: 16px 0 10px;
  border-radius: 2px;
}

.sec-gap { height: 8px; }

.section-block {
  margin-bottom: 6px;
}

.bullet-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #1f1e1a;
  line-height: 1.55;
}

.bullet-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
}

.act-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #1f1e1a;
  line-height: 1.55;
}

.act-num {
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  min-width: 20px;
}

.editable-span {
  outline: none;
  word-break: break-word;
  white-space: pre-wrap;
  &[data-edit] {
    cursor: text;
    border-bottom: 1.5px dashed #bbb;
    &:focus { border-color: #555; }
  }
}

/* ── Image rows ───────────────────────────────────────────────────── */
.img-row {
  display: flex;
  flex-direction: column;

  &--pair {
    flex-direction: row;
    gap: 8px;
    align-items: flex-start;

    .img-item {
      flex: 1;
      min-width: 0;
      max-width: 50%;
    }
  }
}

/* ── Images ───────────────────────────────────────────────────────── */
.img-item {
  position: relative;
  margin: 6px 0 8px;

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 2px;
  }
}

/* ── Image size controls overlay ─────────────────────────────────── */
.img-size-ctrl {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(0,0,0,.6);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  padding: 3px 5px;

  button {
    height: 22px;
    min-width: 22px;
    border-radius: 4px;
    border: none;
    background: transparent;
    color: #ddd;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: background .1s;
    padding: 0 4px;
    &:hover { background: rgba(255,255,255,.2); }
    &.active { background: rgba(255,255,255,.35); color: #fff; }
  }

  .pair-btn {
    font-size: 13px;
    padding: 0 3px;
    margin-left: 2px;
    &.active { background: rgba(99, 200, 130, 0.4); color: #8ef5a0; }
  }
}

.img-size-input {
  width: 32px;
  height: 22px;
  background: transparent;
  border: 1px solid rgba(255,255,255,.25);
  color: #fff;
  border-radius: 4px;
  font-size: 11px;
  text-align: center;
  padding: 0 2px;
  outline: none;
  margin-left: 2px;

  &:focus { border-color: rgba(255,255,255,.6); }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
}

.img-size-pct {
  color: rgba(255,255,255,.5);
  font-size: 11px;
}

/* ── Footer ───────────────────────────────────────────────────────── */
.paper-footer {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(31,30,26,.1);
  padding: 10px 40px;
  font-size: 9px;
  color: #999;
}

/* ── Export loading ───────────────────────────────────────────────── */
.prev-loading {
  position: absolute;
  inset: 0;
  background: rgba(244, 243, 240, 0.82);
  backdrop-filter: blur(4px);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--ink, #1a1a1a);
  font-size: 15px;
}

.prev-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(15, 76, 129, 0.2);
  border-top-color: var(--system-navbar, #0f4c81);
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Mobile ───────────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .prev-bar-title { display: none; }
  .prev-bar-center { justify-content: flex-start; }
  .prev-scroll { padding: 16px 8px 40px; }
  .paper-band { padding: 14px 20px; }
  .paper-body { padding: 20px; }
  .paper-footer { padding: 8px 20px; }
  .band-right { display: none; }
  .img-row--pair { flex-direction: column; .img-item { max-width: 100%; } }
}
</style>
